import { useEffect, useRef, useState } from 'react';
import type { MutableRefObject, RefObject } from 'react';
import { createPortal } from 'react-dom';
import type { WorkbookInstance } from '@fortune-sheet/react';
import { getInternals, type WorkbookInternals } from './workbookInternals';
import { computeSelectionStats, formatStat, cellKey, type SelectionRangeLike, type SelectionStats } from './selectionStats';
import { usePortalContainer } from './usePortalContainer';
import { FortuneCell } from '../types';
import { STATUS_BAR_CONTAINER_SELECTOR } from './selectors';
import styles from './StatusBar.module.scss';

export interface StatusBarProps {
    wrapperRef: RefObject<HTMLElement>;
    workbookRef: RefObject<WorkbookInstance>;
    remountKey: string | number;
    workbookMounted: boolean;
    /** The drawer assigns a `() => void` here on mount — its `hooks.afterSelectionChange`
     *  and content `onOp` handlers call `notifyRef.current?.()` to request a
     *  (rAF-throttled) recompute, bypassing React state batching so a fast
     *  drag's many selection-change calls still coalesce to one compute. */
    notifyRef: MutableRefObject<(() => void) | null>;
}

const readRanges = (wb: WorkbookInstance, internals: WorkbookInternals | null): SelectionRangeLike[] => {
    if (internals) {
        const sel = internals.getContext().luckysheet_select_save;
        if (sel?.length) return sel.map((s) => ({ row: s.row, column: s.column }));
    }
    return (wb.getSelection?.() ?? []).map((s) => ({ row: s.row, column: s.column }));
};

/** One `getCellsByRange` (public API) call per range, flattened into a single
 *  by-coordinate lookup — works identically whether the patch is present. */
const buildCellLookup = (wb: WorkbookInstance, ranges: SelectionRangeLike[]): Map<string, FortuneCell | null> => {
    const map = new Map<string, FortuneCell | null>();
    ranges.forEach((range) => {
        const grid = wb.getCellsByRange({ row: range.row, column: range.column });
        grid.forEach((row, i) => {
            row.forEach((cell, j) => {
                map.set(cellKey(range.row[0] + i, range.column[0] + j), cell as FortuneCell | null);
            });
        });
    });
    return map;
};

/** Sum of each range's rectangle area (overlaps counted twice) — a cheap
 *  upper bound on the deduped cell count. A single-cell click (the by-far
 *  most common selection change) never clears 2 here, so it skips the
 *  `getCellsByRange` fetch entirely instead of paying for it just to find
 *  out the selection is too small to show stats for. */
const maxPossibleCellCount = (ranges: SelectionRangeLike[]): number =>
    ranges.reduce((sum, { row, column }) => sum + (row[1] - row[0] + 1) * (column[1] - column[0] + 1), 0);

/**
 * Portals an app status row into `.fortune-stat-area`, replacing the
 * library's own sum-the-display-string row (hidden via scoped CSS). Shown
 * only for a ≥2-cell selection with at least one non-blank cell;
 * Average/Sum only appear when at least one cell is numeric.
 */
const StatusBar: React.FC<StatusBarProps> = ({ wrapperRef, workbookRef, remountKey, workbookMounted, notifyRef }) => {
    const container = usePortalContainer(wrapperRef, STATUS_BAR_CONTAINER_SELECTOR, remountKey, workbookMounted);
    const [stats, setStats] = useState<SelectionStats | null>(null);
    const rafRef = useRef<number | null>(null);

    useEffect(() => {
        const recompute = (): void => {
            const wb = workbookRef.current;
            if (!wb) return;
            const ranges = readRanges(wb, getInternals(wb));
            if (maxPossibleCellCount(ranges) < 2) {
                setStats(null);
                return;
            }
            const lookup = buildCellLookup(wb, ranges);
            const result = computeSelectionStats(ranges, (r, c) => lookup.get(cellKey(r, c)) ?? null);
            setStats(result.totalCells >= 2 && result.count >= 1 ? result : null);
        };
        const requestRecompute = (): void => {
            if (rafRef.current != null) return; // already scheduled this frame
            rafRef.current = requestAnimationFrame(() => {
                rafRef.current = null;
                recompute();
            });
        };
        notifyRef.current = requestRecompute;
        if (workbookMounted) requestRecompute();
        return () => {
            if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
            notifyRef.current = null;
        };
    }, [workbookRef, workbookMounted, notifyRef]);

    if (!container || !stats) return null;

    const parts: string[] = [];
    if (stats.numericCount >= 1) parts.push(`Average: ${formatStat(stats.average, stats.fa)}`);
    parts.push(`Count: ${stats.count}`);
    if (stats.numericCount >= 1) parts.push(`Sum: ${formatStat(stats.sum, stats.fa)}`);

    return createPortal(<div className={styles.Bar}>{parts.join('  ')}</div>, container);
};

export default StatusBar;
